// 📂 frontend/src/hooks/useAutoRefresh.ts
import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoRefreshOptions {
  interval?: number;
  enabled?: boolean;
  onRefresh?: () => void | Promise<void>;
}

export const useAutoRefresh = ({
  interval = 30000,
  enabled = true,
  onRefresh
}: UseAutoRefreshOptions = {}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeUntilRefresh, setTimeUntilRefresh] = useState(interval);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshTime = useRef<number>(Date.now());

  const refresh = useCallback(async () => {
    if (isRefreshing || !onRefresh) return;
    
    setIsRefreshing(true);
    try {
      await onRefresh();
      lastRefreshTime.current = Date.now();
      setTimeUntilRefresh(interval);
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefresh, interval, isRefreshing]);

  useEffect(() => {
    if (!enabled || !onRefresh) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      refresh();
    }, interval);

    countdownRef.current = setInterval(() => {
      const elapsed = Date.now() - lastRefreshTime.current;
      const remaining = Math.max(0, interval - elapsed);
      setTimeUntilRefresh(remaining);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [enabled, interval]);

  const formatTimeRemaining = useCallback(() => {
    const seconds = Math.floor(timeUntilRefresh / 1000);
    return `${seconds}s`;
  }, [timeUntilRefresh]);

  return {
    isRefreshing,
    refresh,
    formatTimeRemaining
  };
};